/**
 * TokenManager - Secure token storage utility
 *
 * Improves security over localStorage by:
 * 1. Keeping tokens in memory as primary storage (not accessible via XSS)
 * 2. Using sessionStorage as fallback for page refresh (clears on browser close)
 * 3. Centralizing all token operations in one place
 *
 * Security notes:
 * - sessionStorage is less risky than localStorage because it clears when the browser closes
 * - In-memory storage is the most secure but doesn't persist across page refreshes
 * - For production apps requiring higher security, consider HttpOnly cookies with refresh tokens
 */

// Storage keys
const STORAGE_KEYS = {
  TOKEN: 'helix_auth_token',
  USER: 'helix_auth_user',
  IMPERSONATING: 'helix_impersonating',
  ORIGINAL_TOKEN: 'helix_original_token',
  ORIGINAL_USER: 'helix_original_user',
  // CR-4858: Records which of the impersonated user's companies the admin
  // explicitly chose as the access context (e.g. "Login As" was clicked from
  // the Corex users tab, not from the user's primary company). Used by
  // ViewContext to pin the post-reload landing scope so the impersonated
  // session shows the company the admin was inspecting, not the user's
  // default. Only populated when the admin started impersonation through a
  // company-scoped surface; absent for global-impersonation flows.
  IMPERSONATION_CONTEXT: 'helix_impersonation_context',
  AUDIT_LOG_ID: 'helix_impersonation_audit_log_id',
} as const;

// In-memory storage for tokens (most secure)
let memoryToken: string | null = null;
let memoryOriginalToken: string | null = null;

// Persistent login flag — when true, tokens are stored in localStorage
// so they survive browser close. Stored in localStorage itself so we
// know which storage to check on next visit.
const REMEMBER_KEY = 'helix_remember_me';

/**
 * Get the storage backend based on the "remember me" preference.
 * If the user chose "remember me", we use localStorage (persists across sessions).
 * Otherwise, we use sessionStorage (clears when the browser closes).
 */
const getPersistentStorage = (): Storage => {
  return localStorage.getItem(REMEMBER_KEY) === 'true' ? localStorage : sessionStorage;
};

/**
 * Safe JSON parse that handles "undefined", null, and invalid JSON
 */
const safeJSONParse = <T,>(value: string | null): T | null => {
  if (!value || value === 'undefined' || value === 'null') {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Check if a token string is valid (not corrupted)
 */
const isValidTokenString = (token: string | null): token is string => {
  return !!token && token !== 'undefined' && token !== 'null' && token.length > 0;
};

/**
 * TokenManager - Centralized secure token management
 */
export const TokenManager = {
  /**
   * Get the current auth token
   * Priority: memory > sessionStorage
   */
  getToken(): string | null {
    // First check memory (most secure)
    if (isValidTokenString(memoryToken)) {
      return memoryToken;
    }

    // Check the preferred storage (localStorage if "remember me", else sessionStorage)
    const storage = getPersistentStorage();
    const storedToken = storage.getItem(STORAGE_KEYS.TOKEN);
    if (isValidTokenString(storedToken)) {
      memoryToken = storedToken;
      return storedToken;
    }

    // Also check the other storage in case the preference changed
    const otherStorage = storage === localStorage ? sessionStorage : localStorage;
    const otherToken = otherStorage.getItem(STORAGE_KEYS.TOKEN);
    if (isValidTokenString(otherToken)) {
      memoryToken = otherToken;
      return otherToken;
    }

    // Migration: Check legacy localStorage key
    const legacyToken = localStorage.getItem('token');
    if (isValidTokenString(legacyToken)) {
      memoryToken = legacyToken;
      getPersistentStorage().setItem(STORAGE_KEYS.TOKEN, legacyToken);
      localStorage.removeItem('token');
      return legacyToken;
    }

    return null;
  },

  /**
   * Set the auth token
   */
  setToken(token: string): void {
    if (!isValidTokenString(token)) {
      console.warn('TokenManager: Attempted to set invalid token');
      return;
    }
    memoryToken = token;
    getPersistentStorage().setItem(STORAGE_KEYS.TOKEN, token);
  },

  /**
   * Clear the auth token
   */
  clearToken(): void {
    memoryToken = null;
    // Clear from both storages
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem('token');
  },

  /**
   * Get user data from storage
   */
  getUser<T>(): T | null {
    const storage = getPersistentStorage();
    const userData = storage.getItem(STORAGE_KEYS.USER);
    if (userData) {
      return safeJSONParse<T>(userData);
    }

    // Check other storage
    const otherStorage = storage === localStorage ? sessionStorage : localStorage;
    const otherData = otherStorage.getItem(STORAGE_KEYS.USER);
    if (otherData) {
      return safeJSONParse<T>(otherData);
    }

    // Migration from legacy key
    const legacyUser = localStorage.getItem('user');
    if (legacyUser) {
      const parsed = safeJSONParse<T>(legacyUser);
      if (parsed) {
        getPersistentStorage().setItem(STORAGE_KEYS.USER, legacyUser);
        localStorage.removeItem('user');
        return parsed;
      }
    }

    return null;
  },

  /**
   * Set user data
   */
  setUser<T>(user: T): void {
    getPersistentStorage().setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  },

  /**
   * Clear user data
   */
  clearUser(): void {
    sessionStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem('user');
  },

  /**
   * Check if currently impersonating
   */
  isImpersonating(): boolean {
    return sessionStorage.getItem(STORAGE_KEYS.IMPERSONATING) === 'true' ||
           localStorage.getItem('isImpersonating') === 'true';
  },

  /**
   * Get original token (for impersonation)
   */
  getOriginalToken(): string | null {
    if (isValidTokenString(memoryOriginalToken)) {
      return memoryOriginalToken;
    }

    const storedToken = sessionStorage.getItem(STORAGE_KEYS.ORIGINAL_TOKEN);
    if (isValidTokenString(storedToken)) {
      memoryOriginalToken = storedToken;
      return storedToken;
    }

    // Migration from localStorage
    const legacyToken = localStorage.getItem('originalToken');
    if (isValidTokenString(legacyToken)) {
      memoryOriginalToken = legacyToken;
      sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_TOKEN, legacyToken);
      localStorage.removeItem('originalToken');
      return legacyToken;
    }

    return null;
  },

  /**
   * Get original user (for impersonation)
   */
  getOriginalUser<T>(): T | null {
    const sessionUser = sessionStorage.getItem(STORAGE_KEYS.ORIGINAL_USER);
    if (sessionUser) {
      return safeJSONParse<T>(sessionUser);
    }

    // Migration from localStorage
    const legacyUser = localStorage.getItem('originalUser');
    if (legacyUser) {
      const userData = safeJSONParse<T>(legacyUser);
      if (userData) {
        sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_USER, legacyUser);
        localStorage.removeItem('originalUser');
        return userData;
      }
    }

    return null;
  },

  /**
   * Start impersonation - store original user/token
   */
  startImpersonation<T>(originalUser: T, originalToken: string, auditLogId?: number): void {
    memoryOriginalToken = originalToken;
    sessionStorage.setItem(STORAGE_KEYS.IMPERSONATING, 'true');
    sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_TOKEN, originalToken);
    sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_USER, JSON.stringify(originalUser));
    if (auditLogId) {
      sessionStorage.setItem(STORAGE_KEYS.AUDIT_LOG_ID, String(auditLogId));
    }
  },

  /**
   * CR-4858: Persist the admin-chosen company context for an impersonation
   * session. Set before the post-impersonation reload so ViewContext can
   * read it on rehydration and pin the landing scope to that company.
   */
  setImpersonationContext(context: { id: number; name: string }): void {
    sessionStorage.setItem(STORAGE_KEYS.IMPERSONATION_CONTEXT, JSON.stringify(context));
  },

  /**
   * CR-4858: Read the admin-chosen company context for the active
   * impersonation session, or null if none was set (global Login As flows).
   */
  getImpersonationContext(): { id: number; name: string } | null {
    return safeJSONParse<{ id: number; name: string }>(
      sessionStorage.getItem(STORAGE_KEYS.IMPERSONATION_CONTEXT)
    );
  },

  /**
   * Stop impersonation - clear original user/token
   */
  stopImpersonation(): void {
    memoryOriginalToken = null;
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATING);
    sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_CONTEXT);
    sessionStorage.removeItem(STORAGE_KEYS.AUDIT_LOG_ID);
    // Clean up legacy localStorage
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('originalUser');
  },

  getAuditLogId(): number | null {
    const val = sessionStorage.getItem(STORAGE_KEYS.AUDIT_LOG_ID);
    return val ? parseInt(val, 10) : null;
  },

  /**
   * Clear all auth data (logout)
   */
  /**
   * Set the "remember me" preference.
   * When true, tokens are stored in localStorage (survive browser close).
   * When false, tokens are stored in sessionStorage (clear on close).
   */
  setRememberMe(remember: boolean): void {
    if (remember) {
      localStorage.setItem(REMEMBER_KEY, 'true');
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }
  },

  clearAll(): void {
    memoryToken = null;
    memoryOriginalToken = null;

    // Clear both storages
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.USER);
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATING);
    sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
    sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_CONTEXT);

    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(REMEMBER_KEY);

    // Clean up legacy items
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('originalUser');
  },

  /**
   * Check if there's a valid token stored
   */
  hasToken(): boolean {
    return isValidTokenString(this.getToken());
  },
};

export default TokenManager;
